/**
 * Fias Transfer — public download-page hooks (share-token auth).
 *
 * Cache keys live under `queryKeys.fiasTransferPublic`, deliberately a
 * separate root from the sender's `queryKeys.fiasTransfer`: a sender-side
 * invalidation must never evict a visitor's state, and the two surfaces share
 * no identifiers (a visitor only ever knows the raw token).
 *
 * **Error handling contract.** Every endpoint here returns a UNIFORM 404 for
 * unknown, revoked, expired, exhausted, unpaid, and deleted links. That is a
 * security control — distinguishing them would make the endpoint an existence
 * oracle. So these hooks must never map a 404 to a specific cause, and UI copy
 * must not speculate about why a link is unavailable.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, {
  FiasTransferPublicSummary,
  FiasTransferManifest,
  FiasTransferQuestion,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/** The single visitor-facing message for every inaccessible state. */
export const TRANSFER_UNAVAILABLE_MESSAGE = 'This link is not available.';

export function usePublicTransferSummary(token: string | undefined): {
  summary: FiasTransferPublicSummary | null;
  loading: boolean;
  /** True for ANY inaccessible state. Never says which. */
  unavailable: boolean;
  refresh: () => void;
} {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.fiasTransferPublic.summary(token ?? ''),
    queryFn: () => api.getFiasTransferPublicSummary(token!),
    enabled: !!token,
    // A token that does not resolve will not start resolving on retry, and
    // retrying amplifies load against an endpoint anyone can hit.
    retry: false,
  });

  return {
    summary: data ?? null,
    loading: isLoading,
    unavailable: !!error,
    refresh: () => void refetch(),
  };
}

/**
 * The file list. Only fetched once every gate on the link has been passed —
 * filenames and folder paths are themselves confidential, so the caller must
 * not enable this until `summary.unlocked` is satisfied.
 */
export function usePublicTransferManifest(
  token: string | undefined,
  enabled: boolean,
): { manifest: FiasTransferManifest | null; loading: boolean; refresh: () => void } {
  const { data, isLoading, refetch } = useQuery({
    queryKey: queryKeys.fiasTransferPublic.manifest(token ?? ''),
    queryFn: () => api.getFiasTransferManifest(token!),
    enabled: !!token && enabled,
    retry: false,
  });

  return { manifest: data ?? null, loading: isLoading, refresh: () => void refetch() };
}

export function usePublicTransferActions(token: string | undefined) {
  const queryClient = useQueryClient();

  const invalidateSummary = () => {
    if (token) {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.fiasTransferPublic.summary(token),
      });
    }
  };

  const unlock = useMutation({
    mutationFn: (password: string) => api.unlockFiasTransfer(token!, password),
    onSuccess: invalidateSummary,
  });

  const acceptNda = useMutation({
    mutationFn: () => api.acceptFiasTransferNda(token!),
    onSuccess: invalidateSummary,
  });

  const recordEmail = useMutation({
    mutationFn: (email: string) => api.recordFiasTransferEmail(token!, email),
    onSuccess: invalidateSummary,
  });

  /**
   * Opens the download window, consuming one of the sender's authorizations.
   * The server makes this idempotent while a window is already open, so a
   * double-click cannot burn two — but the UI should still disable the control
   * while it is in flight rather than relying on that.
   */
  const openWindow = useMutation({
    mutationFn: () => api.openFiasTransferWindow(token!),
    onSuccess: () => {
      invalidateSummary();
      if (token) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.fiasTransferPublic.manifest(token),
        });
      }
    },
  });

  return { unlock, acceptNda, recordEmail, openWindow };
}

/**
 * The visitor's own Q&A thread.
 *
 * Session-scoped SERVER-side — the client cannot widen it, and must not imply
 * otherwise in copy. An open link is one token shared with many people, so
 * showing other holders' questions would leak between investors.
 */
export function usePublicTransferQuestions(token: string | undefined): {
  questions: FiasTransferQuestion[];
  loading: boolean;
  ask: (body: string) => Promise<unknown>;
  asking: boolean;
} {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.fiasTransferPublic.questions(token ?? ''),
    queryFn: () => api.getFiasTransferQuestions(token!),
    enabled: !!token,
    retry: false,
  });

  const ask = useMutation({
    mutationFn: (body: string) => api.askFiasTransferQuestion(token!, body),
    onSuccess: () => {
      if (token) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.fiasTransferPublic.questions(token),
        });
      }
    },
  });

  return {
    questions: data?.questions ?? [],
    loading: isLoading,
    ask: (body: string) => ask.mutateAsync(body),
    asking: ask.isPending,
  };
}

/**
 * Fetch a signed URL and hand it to the browser.
 *
 * Navigation rather than fetch-then-blob: the URL is short-lived (10 minutes,
 * far shorter than the platform's 1-hour default because this surface hands
 * URLs to anonymous parties), and buffering a multi-GB file into memory to
 * re-emit it would defeat the point of a presigned download.
 */
export async function downloadTransferFile(
  token: string,
  documentId: string,
): Promise<{ notScannable: boolean }> {
  const { downloadUrl, notScannable } = await api.getFiasTransferFileUrl(token, documentId);
  window.location.assign(downloadUrl);
  // Reported back so the page can mark the file. `UNSUPPORTED` is the one
  // malware verdict that is still served (GuardDuty could not inspect the
  // format), and the recipient should not be left assuming it was checked.
  return { notScannable: notScannable === true };
}
